import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Image as ImageIcon, Info, Megaphone as DefaultPopupIcon, Pencil, Plus, Power, Trash2, X } from 'lucide-react';
import { api } from '@/services/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { compressImage } from '@/utils/imageCompress';
import useImageCropper from '@/hooks/useImageCropper';

const CTA_TYPES = [
  { value: 'survey', es: 'Nueva Encuesta', en: 'New Survey', icon: DefaultPopupIcon },
  { value: 'benefit', es: 'Nuevo Beneficio', en: 'New Benefit', icon: DefaultPopupIcon },
  { value: 'info', es: 'Comunicado general', en: 'General announcement', icon: Info },
];

const emptyPopupForm = () => ({
  title: '',
  body: '',
  image_url: '',
  cta_type: 'info',
  cta_label: '',
  target_houses: [],
  is_active: true,
});

const ctaMeta = (value) => CTA_TYPES.find((type) => type.value === value) || CTA_TYPES[2];

export default function PopupsSection({ emptyStateIcon: EmptyStateIcon = DefaultPopupIcon }) {
  const Megaphone = EmptyStateIcon;
  const { language } = useLanguage();
  const { theme } = useTheme();
  const es = language === 'es';

  const [popups, setPopups] = useState([]);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyPopupForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { cropImage, cropperNode } = useImageCropper();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [popupData, houseData] = await Promise.all([api.getPopups(), api.getHouses()]);
      setPopups(Array.isArray(popupData) ? popupData : []);
      setHouses(Array.isArray(houseData) ? houseData : []);
    } catch (e) {
      setError(e?.response?.data?.detail || (es ? 'No se pudieron cargar los pop-ups.' : 'Pop-ups could not be loaded.'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyPopupForm());
    setShowForm(true);
  };

  const openEdit = (popup) => {
    setEditingId(popup.id);
    setForm({
      title: popup.title || '',
      body: popup.body || '',
      image_url: '',
      cta_type: popup.cta_type || 'info',
      cta_label: popup.cta_label || '',
      target_houses: popup.target_houses || [],
      is_active: popup.is_active !== false,
    });
    setShowForm(true);
  };

  const toggleHouse = (houseId) => {
    setForm((current) => {
      const has = current.target_houses.includes(houseId);
      return {
        ...current,
        target_houses: has
          ? current.target_houses.filter((id) => id !== houseId)
          : [...current.target_houses, houseId],
      };
    });
  };

  const handleImage = async (event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = '';
    if (!file) return;
    try {
      const cropped = await cropImage(file, {
        aspect: 4 / 3,
        title: es ? 'Encuadrar flyer' : 'Frame flyer',
      });
      if (!cropped) return;
      const dataUrl = await compressImage(cropped, { maxWidth: 1200, maxHeight: 900, quality: 0.78 });
      setForm((current) => ({ ...current, image_url: dataUrl }));
    } catch (e) {
      console.error('Error compressing pop-up image:', e);
      setError(es ? 'No se pudo procesar la imagen.' : 'The image could not be processed.');
    }
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      if (!form.title.trim()) throw new Error(es ? 'Completá el título del pop-up.' : 'Fill in the pop-up title.');
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        image_url: form.image_url || undefined,
        cta_type: form.cta_type,
        cta_label: form.cta_label.trim() || undefined,
        target_houses: form.target_houses,
        is_active: !!form.is_active,
      };
      if (editingId) {
        await api.updatePopup(editingId, payload);
      } else {
        await api.createPopup(payload);
      }
      setShowForm(false);
      setForm(emptyPopupForm());
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || (es ? 'No se pudo guardar el pop-up.' : 'Pop-up could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (popup) => {
    try {
      await api.updatePopup(popup.id, { is_active: popup.is_active === false });
      setPopups((current) => current.map((p) => (p.id === popup.id ? { ...p, is_active: popup.is_active === false } : p)));
    } catch (e) {
      setError(e?.response?.data?.detail || (es ? 'No se pudo actualizar el pop-up.' : 'Pop-up could not be updated.'));
    }
  };

  const removePopup = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError('');
    try {
      await api.deletePopup(deleteTarget.id);
      setPopups((current) => current.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      setError(e?.response?.data?.detail || (es ? 'No se pudo eliminar el pop-up.' : 'Pop-up could not be deleted.'));
    } finally {
      setDeleting(false);
    }
  };

  const targetLabel = (popup) => {
    if (!popup.target_houses?.length) return es ? 'Todas las casas' : 'All houses';
    const names = popup.target_houses.map((id) => houses.find((house) => house.id === id)?.name).filter(Boolean);
    return names.length ? names.join(', ') : `${popup.target_houses.length}`;
  };

  return (
    <div className="space-y-5 animate-enter" data-testid="admin-popups-section">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">
            {es ? 'Pop-ups' : 'Pop-ups'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {es ? 'Publicá avisos informativos que se muestran a los huéspedes al abrir la app.' : 'Publish informational notices shown to guests when they open the app.'}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl px-5 text-sm font-semibold shadow-sm transition-opacity hover:opacity-90"
          style={theme === 'light' ? { backgroundColor: '#111111', color: '#ffffff' } : { backgroundColor: '#ffffff', color: '#111111' }}
          data-testid="new-popup-button"
        >
          <Plus className="h-4 w-4" />
          {es ? 'Nuevo pop-up' : 'New pop-up'}
        </button>
      </div>

      {error && !showForm && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}

      {loading ? (
        <div className="admin-content-surface rounded-2xl border border-border bg-surface p-10 text-center shadow-none">
          <p className="text-muted-foreground">{es ? 'Cargando…' : 'Loading…'}</p>
        </div>
      ) : popups.length === 0 ? (
        <div className="admin-content-surface rounded-2xl border border-border bg-surface p-10 text-center shadow-none">
          <Megaphone className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-muted-foreground">{es ? 'No hay pop-ups todavía.' : 'No pop-ups yet.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {popups.map((popup) => {
            const meta = ctaMeta(popup.cta_type);
            return (
              <div
                key={popup.id}
                className="admin-content-surface rounded-2xl border border-border bg-surface p-4 shadow-none"
                data-testid={`popup-card-${popup.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-heading text-base font-semibold text-foreground">{popup.title}</h3>
                      <span className="rounded-full border border-[color:color-mix(in_srgb,#3A9CC8_30%,transparent)] bg-[color:color-mix(in_srgb,#3A9CC8_12%,transparent)] px-2 py-0.5 text-[11px] font-medium text-[#3A9CC8]">
                        {es ? meta.es : meta.en}
                      </span>
                      {popup.is_active === false ? (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          {es ? 'Inactivo' : 'Inactive'}
                        </span>
                      ) : (
                        <span className="rounded-full border border-[color:color-mix(in_srgb,#22c55e_25%,transparent)] bg-[color:color-mix(in_srgb,#22c55e_12%,transparent)] px-2 py-0.5 text-[11px] font-medium text-[color:color-mix(in_srgb,#16a34a_80%,var(--fg))]">
                          {es ? 'Activo' : 'Active'}
                        </span>
                      )}
                    </div>
                    {popup.body && <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{popup.body}</p>}
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {es ? 'Visible para:' : 'Visible to:'} <span className="font-medium text-foreground">{targetLabel(popup)}</span>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      onClick={() => openEdit(popup)}
                      className="rounded-lg p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                      aria-label={es ? 'Editar' : 'Edit'}
                      data-testid={`popup-edit-${popup.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => toggleActive(popup)}
                      className="rounded-lg p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                      aria-label={es ? (popup.is_active === false ? 'Reactivar' : 'Archivar') : (popup.is_active === false ? 'Reactivate' : 'Archive')}
                      title={es ? (popup.is_active === false ? 'Reactivar' : 'Archivar') : (popup.is_active === false ? 'Reactivate' : 'Archive')}
                      data-testid={`popup-toggle-${popup.id}`}
                    >
                      <Power className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(popup)}
                      className="rounded-lg p-2 text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive"
                      aria-label={es ? 'Eliminar' : 'Delete'}
                      title={es ? 'Eliminar definitivamente' : 'Delete permanently'}
                      data-testid={`popup-delete-${popup.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="popup-form-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => !saving && setShowForm(false)} aria-hidden="true" />
          <div className="relative w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="font-heading text-lg font-bold text-foreground">
                {editingId ? (es ? 'Editar pop-up' : 'Edit pop-up') : (es ? 'Nuevo pop-up' : 'New pop-up')}
              </h2>
              <button onClick={() => setShowForm(false)} className="rounded-lg p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              {error && <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {es ? 'Título' : 'Title'}
                </label>
                <input
                  value={form.title}
                  onChange={(e) => setForm((c) => ({ ...c, title: e.target.value }))}
                  placeholder={es ? 'Ej. ¡Nueva encuesta disponible!' : 'E.g. New survey available!'}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {es ? 'Texto' : 'Body'}
                </label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm((c) => ({ ...c, body: e.target.value }))}
                  rows={3}
                  placeholder={es ? 'Texto informativo del pop-up…' : 'Informational pop-up text…'}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {es ? 'Flyer / imagen (opcional)' : 'Flyer / image (optional)'}
                </label>
                {form.image_url ? (
                  <div className="relative overflow-hidden rounded-lg border border-border">
                    <img src={form.image_url} alt="" className="max-h-56 w-full object-contain bg-muted/30" />
                    <button
                      type="button"
                      onClick={() => setForm((c) => ({ ...c, image_url: '' }))}
                      className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white transition hover:bg-black/80"
                      aria-label={es ? 'Quitar imagen' : 'Remove image'}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border bg-muted/20 px-3 py-6 text-center transition hover:bg-muted/40">
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{es ? 'Subir imagen (formato apaisado recomendado)' : 'Upload image (landscape recommended)'}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleImage} />
                  </label>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {es ? 'Acción del botón (CTA)' : 'Button action (CTA)'}
                </label>
                <div className="relative">
                  <select
                    value={form.cta_type}
                    onChange={(e) => setForm((c) => ({ ...c, cta_type: e.target.value }))}
                    className="h-11 w-full appearance-none rounded-xl border border-border bg-surface px-3 pr-9 text-sm font-medium text-foreground outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
                  >
                    {CTA_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>{es ? type.es : type.en}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  {es
                    ? '“Nueva Encuesta” abre la encuesta, “Nuevo Beneficio” va a Beneficios y “Comunicado general” muestra un botón de cierre.'
                    : '“New Survey” opens the survey, “New Benefit” goes to Benefits and “General announcement” shows a close button.'}
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {es ? 'Texto del botón (opcional)' : 'Button label (optional)'}
                </label>
                <input
                  value={form.cta_label}
                  onChange={(e) => setForm((c) => ({ ...c, cta_label: e.target.value }))}
                  placeholder={es ? 'Por defecto según la acción' : 'Defaults to the action'}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {es ? 'Casas (sin selección = todas)' : 'Houses (no selection = all)'}
                </label>
                <div className="grid gap-1.5 rounded-lg border border-border bg-muted/10 p-2 max-h-40 overflow-y-auto">
                  {houses.map((house) => {
                    const selected = form.target_houses.includes(house.id);
                    return (
                      <label key={house.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition hover:bg-muted/50">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleHouse(house.id)}
                          className="h-4 w-4 accent-amber-500"
                        />
                        <span className="text-foreground">{house.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((c) => ({ ...c, is_active: e.target.checked }))}
                  className="h-4 w-4 accent-amber-500"
                />
                {es ? 'Activo' : 'Active'}
              </label>
            </div>

            <div className="flex gap-2 border-t border-border px-5 py-4">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                disabled={saving}
                className="flex-1 rounded-lg border border-border bg-surface py-2.5 text-sm font-semibold text-foreground transition hover:bg-muted disabled:opacity-50"
              >
                {es ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="flex-1 rounded-lg py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                style={theme === 'light' ? { backgroundColor: '#111111' } : { backgroundColor: '#ffffff', color: '#111111' }}
              >
                {saving ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar' : 'Save')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {deleteTarget && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="popup-delete-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => !deleting && setDeleteTarget(null)} aria-hidden="true" />
          <div className="relative w-full max-w-sm rounded-2xl border border-border bg-surface p-5 shadow-xl">
            <div className="mb-3 flex items-start gap-3">
              <span className="rounded-full bg-destructive/10 p-2 text-destructive">
                <Trash2 className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="font-heading text-base font-bold text-foreground">
                  {es ? 'Eliminar pop-up' : 'Delete pop-up'}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {es
                    ? 'Se borra definitivamente, junto con su imagen y las marcas de "visto" de los usuarios. Esta acción no se puede deshacer.'
                    : 'It is permanently removed, along with its image and users "seen" marks. This action cannot be undone.'}
                </p>
              </div>
            </div>
            <p className="mb-4 truncate rounded-lg bg-muted/40 px-3 py-2 text-sm font-medium text-foreground">{deleteTarget.title}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 rounded-lg border border-border bg-surface py-2.5 text-sm font-semibold text-foreground transition hover:bg-muted disabled:opacity-50"
              >
                {es ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={removePopup}
                disabled={deleting}
                className="flex-1 rounded-lg bg-destructive py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                data-testid="popup-delete-confirm"
              >
                {deleting ? (es ? 'Eliminando…' : 'Deleting…') : (es ? 'Eliminar' : 'Delete')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {cropperNode}
    </div>
  );
}
